import { type ReactNode, useMemo } from 'react';
import { useParams } from 'react-router';

import HomeownerAssociationContext from '@/containers/HomeownerAssociation/context/HomeownerAssociationContext';
import { type RouteParams } from '@/types';
import { trpc } from '@/config/trpc';
import { useGetHomeownerAssociationDivisionKeys } from '@/queries';

import { PinnedSubNavProvider } from '@/containers/HomeownerAssociation/context/PinnedSubNavContext';

type HomeownerAssociationContextProps = {
  children: ReactNode;
};

const queryOptions = {
  trpc: {
    context: {
      skipBatch: true,
    },
  },
};

const HomeownerAssociationContextProvider: React.FC<HomeownerAssociationContextProps> = ({
  children,
}) => {
  const trpcUtils = trpc.useUtils();

  const { buildingId, homeownerAssociationId } = useParams() as RouteParams;

  const { data: homeownerAssociation, isLoading: homeownerAssociationIsLoading } =
    trpc.hoa.summary.useQuery(
      { id: homeownerAssociationId },
      { ...queryOptions, enabled: !!homeownerAssociationId },
    );

  const { data: buildings, isLoading: buildingsAreLoading } = trpc.building.summariesByIds.useQuery(
    homeownerAssociation?.buildings as string[],
    { ...queryOptions, enabled: !!homeownerAssociation?.buildings?.length },
  );

  const { data: units, isLoading: unitsAreLoading } = trpc.building.unit.listForBuildings.useQuery(
    homeownerAssociation?.buildings as string[],
    { ...queryOptions, enabled: !!homeownerAssociation?.buildings?.length },
  );

  const { data: relations, isLoading: relationsAreLoading } =
    trpc.building.relationsForBuildings.useQuery(homeownerAssociation?.buildings as string[], {
      ...queryOptions,
      enabled: !!homeownerAssociation?.buildings?.length,
    });

  const { data: glas, isLoading: glasAreLoading } = trpc.hoa.account.list.useQuery(
    { homeownerAssociationId },
    { ...queryOptions, enabled: !!homeownerAssociationId },
  );

  const { homeownerAssociationDivisionKeys, areHomeownerAssociationDivisionKeysLoading } =
    useGetHomeownerAssociationDivisionKeys(homeownerAssociationId);

  const value = useMemo(
    () => ({
      buildings: buildings || [],
      buildingsAreLoading,
      divisionKeys: homeownerAssociationDivisionKeys ?? [],
      divisionKeysAreLoading: areHomeownerAssociationDivisionKeysLoading,
      glas: glas ?? [],
      glasAreLoading,
      homeownerAssociation,
      homeownerAssociationId: homeownerAssociationId ?? null,
      homeownerAssociationIsLoading,
      invalidateGlas: () => {
        trpcUtils.hoa.account.list.invalidate({
          homeownerAssociationId,
        });

        trpcUtils.hoa.account.autocomplete.invalidate({
          homeownerAssociationId,
        });
      },
      navigatedFromBuildingId: buildingId,
      relations: relations ?? [],
      relationsAreLoading,
      units: units ?? [],
      unitsAreLoading,
    }),
    [
      buildings,
      buildingsAreLoading,
      homeownerAssociationDivisionKeys,
      areHomeownerAssociationDivisionKeysLoading,
      glas,
      glasAreLoading,
      homeownerAssociation,
      homeownerAssociationId,
      homeownerAssociationIsLoading,
      buildingId,
      relations,
      relationsAreLoading,
      units,
      unitsAreLoading,
      trpcUtils.hoa.account.list,
      trpcUtils.hoa.account.autocomplete,
    ],
  );

  return (
    <HomeownerAssociationContext.Provider value={value}>
      <PinnedSubNavProvider>{children}</PinnedSubNavProvider>
    </HomeownerAssociationContext.Provider>
  );
};

export default HomeownerAssociationContextProvider;
